// ImageCarousel.jsx
"use client";

import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Heart, X, ZoomIn } from "lucide-react";
import { useCarousel } from "../Inspiracion.jsx";
import PropTypes from 'prop-types';
import '../Inspiracion.css';

/**
 * Carrusel de imágenes con controles, favoritos y vista ampliada.
 */
export default function ImageCarousel({ images, autoplayDelay, onToggleFavorito, esFavorito }) {
  const { currentIndex, goToNext, goToPrev, goToIndex } = useCarousel(
    images.length,
    autoplayDelay
  );
  const [ampliada, setAmpliada] = useState(false);

  const total = images ? images.length : 0;
  const actual = total ? images[currentIndex] : null;

  const cerrar = useCallback(() => setAmpliada(false), []);
  const abrir = useCallback(() => setAmpliada(true), []);

  // Escape cierra la vista ampliada
  useEffect(() => {
    if (!ampliada) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') cerrar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ampliada, cerrar]);

  // Bloquea el scroll del fondo mientras está abierta
  useEffect(() => {
    if (!ampliada) return undefined;

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflowAnterior;
    };
  }, [ampliada]);

  if (!images || images.length === 0) {
    return <div className="insp-carousel"><div className="insp-placeholder">No hay imágenes disponibles</div></div>;
  }

  const handleError = e => { e.target.src = "/placeholder.svg"; };

  return (
    <div className="insp-carousel">
      {images.length > 1 && (
        <>
          <button className="insp-control insp-control--prev" onClick={goToPrev} aria-label="Imagen anterior">
            <ChevronLeft className="insp-icon" />
          </button>
          <button className="insp-control insp-control--next" onClick={goToNext} aria-label="Imagen siguiente">
            <ChevronRight className="insp-icon" />
          </button>
        </>
      )}

      {images.length > 1 && (
        <div className="insp-counter" aria-hidden="true">
          <span className="insp-counter-current">{currentIndex + 1}</span>
          <span className="insp-counter-div">/</span>
          <span>{images.length}</span>
        </div>
      )}

      <div className="insp-track" role="region" aria-roledescription="carrusel" aria-label="Galería de imágenes">
        {images.map((img, idx) => (
          <div
            key={img.id}
            className={`insp-slide ${currentIndex === idx ? 'insp-slide--active' : ''}`}
            style={{ transform: `translateX(${(idx - currentIndex) * 100}%)`, opacity: currentIndex === idx ? 1 : 0 }}
            role="group"
            aria-roledescription="diapositiva"
            aria-label={`${img.titulo} - ${idx + 1} de ${images.length}`}
            aria-hidden={currentIndex !== idx}
          >
            <div className="insp-image-wrapper">
              <button
                type="button"
                className="insp-image-button"
                onClick={() => { goToIndex(idx); abrir(); }}
                aria-label={`Ampliar ${img.titulo}`}
                tabIndex={currentIndex === idx ? 0 : -1}
              >
                <img src={img.url} alt={img.titulo} onError={handleError} className="insp-image" loading="lazy" />
              </button>
              <div className="insp-info">
                <div className="insp-info-content">
                  <h3 className="insp-title">{img.titulo}</h3>
                  <p className="insp-credits">{img.creditos}</p>
                </div>
                <div className="insp-info-actions">
                  <button
                    type="button"
                    className="insp-favorite-btn"
                    onClick={() => onToggleFavorito(img.id)}
                    aria-label={esFavorito(img.id) ? "Quitar de favoritos" : "Añadir a favoritos"}
                    aria-pressed={esFavorito(img.id)}
                  >
                    <Heart className="insp-icon-heart" fill={esFavorito(img.id) ? "#ff4757" : "none"} />
                  </button>
                  <button
                    type="button"
                    className="insp-zoom-btn"
                    onClick={() => { goToIndex(idx); abrir(); }}
                    aria-label={`Ver ${img.titulo} a detalle`}
                  >
                    <ZoomIn size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {images.length > 1 && (
        <div className="insp-indicators">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`insp-indicator ${currentIndex === idx ? 'insp-indicator--active' : ''}`}
              onClick={() => goToIndex(idx)}
              aria-label={`Ir a imagen ${idx + 1}`}
              aria-current={currentIndex === idx}
            />
          ))}
        </div>
      )}

      {ampliada && actual && (
        <div
          className="insp-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${actual.titulo} a detalle`}
          onClick={cerrar}
        >
          <button
            type="button"
            className="insp-lightbox-close"
            onClick={cerrar}
            aria-label="Cerrar"
          >
            <X size={22} aria-hidden="true" />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                className="insp-lightbox-nav insp-lightbox-nav--prev"
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                aria-label="Imagen anterior"
              >
                <ChevronLeft className="insp-icon" />
              </button>
              <button
                type="button"
                className="insp-lightbox-nav insp-lightbox-nav--next"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                aria-label="Imagen siguiente"
              >
                <ChevronRight className="insp-icon" />
              </button>
            </>
          )}

          <figure className="insp-lightbox-figure" onClick={(e) => e.stopPropagation()}>
            <img
              src={actual.url}
              alt={actual.titulo}
              className="insp-lightbox-image"
              onError={handleError}
            />
            <figcaption className="insp-lightbox-caption">
              <span className="insp-lightbox-title">{actual.titulo}</span>
              <span className="insp-lightbox-credits">{actual.creditos}</span>
              {images.length > 1 && (
                <span className="insp-lightbox-count">
                  {currentIndex + 1} / {images.length}
                </span>
              )}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired, url: PropTypes.string.isRequired, titulo: PropTypes.string, creditos: PropTypes.string })).isRequired,
  autoplayDelay: PropTypes.number,
  onToggleFavorito: PropTypes.func.isRequired,
  esFavorito: PropTypes.func.isRequired,
};